import ModalPanel, { modalPanelWidth } from '@/common/atoms/ModalPanel'
import { Title } from '@/common/atoms/Title'
import { useElementSize } from '@/common/hooks/useElementSize'
import useIsSidePanel from '@/common/hooks/useIsSidePanel'
import useOverflowHidden from '@/common/hooks/useOverflowHidden'
import useUpdatableQueryParams from '@/common/hooks/useUpdatableQueryParams'
import CirclesGraph from '@/graph/CirclesGraph'
import { GraphProvider } from '@/graph/contexts/GraphContext'
import useGraphContextMenu from '@/graph/hooks/useGraphContextMenu'
import useGraphEvents from '@/graph/hooks/useGraphEvents'
import useGraphView from '@/graph/hooks/useGraphView'
import { CirclesGraphViews } from '@/graph/types'
import { SidebarContext } from '@/layout/contexts/SidebarContext'
import MemberContent from '@/member/components/MemberContent'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import useMyRoles from '@/member/hooks/useMyRoles'
import { useOrgContext } from '@/org/contexts/OrgContext'
import useVacantRoles from '@/role/hooks/useVacantRoles'
import WhiteboardSaveStatus from '@/whiteboard/components/WhiteboardSaveStatus'
import WhiteboardView from '@/whiteboard/components/WhiteboardView'
import { WhiteboardSaveState } from '@/whiteboard/whiteboardSaver'
import { Box, Flex, useColorMode } from '@chakra-ui/react'
import { HighlightTone } from '@rolebase/graph'
import { GraphView } from '@rolebase/shared/model/graph'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { CirclesPanel, isCirclesPanel } from '../circlesPanels'
import CircleContent from '../components/CircleContent'
import CirclesGraphOptions from '../components/CirclesGraphOptions'
import CirclesPanelContent from '../components/CirclesPanelContent'
import RoleLinksGraph from '../components/RoleLinksGraph'
import { CircleProvider } from '../contexts/CIrcleContext'

type CirclesPageParams = {
  circleId: string
  memberId: string
  parentId: string
  view: string
  folded: string
  members: string
  panel: string
}

enum Panels {
  None,
  Circle,
  Member,
  // A panel of the settings menu, named by the `panel` query param
  Settings,
}

export default function CirclesPage() {
  const { t } = useTranslation()
  const sidebarContext = useContext(SidebarContext)

  // On desktop (lg+) the panel is a fixed side panel and the page does not
  // scroll. Below lg the graph and panel stack vertically and the whole page
  // scrolls as one, the graph taking the first part of the screen.
  const isSidePanel = useIsSidePanel()
  useOverflowHidden(isSidePanel)

  const { params: queryParams, changeParams } =
    useUpdatableQueryParams<CirclesPageParams>()
  const { org, orgData } = useOrgContext()
  const [ready, setReady] = useState(false)

  // Content size
  const boxRef = useRef<HTMLDivElement>(null)
  const boxSize = useElementSize(boxRef)

  // Panels
  const [panel, setPanel] = useState<Panels>(Panels.None)
  const [settingsPanel, setSettingsPanel] = useState<CirclesPanel | undefined>()
  const [circleId, setCircleId] = useState<string | undefined>()
  const [memberId, setMemberId] = useState<string | null | undefined>()
  const [parentId, setParentId] = useState<string | undefined>()

  // Graph view, kept in the URL like the selected circle so it is shareable
  // and survives navigation. Falls back to the organization default.
  const graphView = useGraphView()

  // The links view (#107) draws its own web, outside the layout engine: there
  // is no engine to wait for, and opening a role from the URL must work there
  // too
  const isRoleLinks = graphView.view === CirclesGraphViews.RoleLinks
  // Same for the whiteboard (#200), which is not a graph at all
  const isWhiteboard = graphView.view === CirclesGraphViews.Whiteboard
  // Its save status sits top right, in place of the settings menu (Yohan,
  // recette du 13/10)
  const [whiteboardSave, setWhiteboardSave] =
    useState<WhiteboardSaveState>('idle')
  useEffect(() => {
    if (isRoleLinks || isWhiteboard) setReady(true)
  }, [isRoleLinks, isWhiteboard])

  // Graph events, plus the right click menu rendered below the graph
  const graphEvents = useGraphEvents()
  const { events: contextMenuEvents, contextMenu } = useGraphContextMenu({
    view: graphView.view,
  })
  const events = useMemo(
    () => ({ ...graphEvents, ...contextMenuEvents }),
    [graphEvents, contextMenuEvents]
  )

  const handleViewChange = useCallback(
    ({ view, folded, members }: GraphView) =>
      changeParams({
        view,
        folded: folded ? '1' : undefined,
        members: members ? undefined : '0',
      }),
    [changeParams]
  )

  const handleClosePanel = useCallback(
    () =>
      changeParams({
        circleId: undefined,
        memberId: undefined,
        parentId: undefined,
        panel: undefined,
      }),
    [changeParams]
  )

  // Emphasis of the org chart: ephemeral UI toggle, no persistence. When on,
  // "My roles" (orange) or the vacant roles (violet, #116) are emphasized and
  // the rest dimmed, in every view of the engine. One at a time: turning one on
  // turns the other off.
  const [highlight, setHighlight] = useState<HighlightTone | undefined>()
  const myRoles = useMyRoles()
  const vacantRoles = useVacantRoles()
  const currentMember = useCurrentMember()
  const highlightedCircleIds = useMemo(() => {
    if (highlight === 'mine') return myRoles.map((circle) => circle.id)
    if (highlight === 'vacant') return vacantRoles.map((circle) => circle.id)
    return undefined
  }, [highlight, myRoles, vacantRoles])

  // Toggling on also opens my own member panel, so a single click gives both
  // the highlighted circles and the list of my roles. Toggling off closes only
  // the panel it opened: if another circle or member has been selected
  // meanwhile, drop the highlight but leave that panel alone.
  const handleToggleMyRoles = useCallback(() => {
    const next = highlight !== 'mine'
    setHighlight(next ? 'mine' : undefined)
    if (!currentMember) return
    if (next) {
      changeParams({
        memberId: currentMember.id,
        circleId: undefined,
        parentId: undefined,
        panel: undefined,
      })
    } else if (queryParams.memberId === currentMember.id) {
      changeParams({ memberId: undefined })
    }
  }, [highlight, currentMember, queryParams.memberId, changeParams])

  // Same for the vacant roles, with their panel. Toggling off closes the panel
  // only while it is still the vacant roles list: once a role of the list has
  // been opened, that role stays.
  const handleToggleVacantRoles = useCallback(() => {
    const next = highlight !== 'vacant'
    setHighlight(next ? 'vacant' : undefined)
    if (next) {
      changeParams({
        panel: 'vacantRoles',
        circleId: undefined,
        memberId: undefined,
        parentId: undefined,
      })
    } else if (
      queryParams.panel === 'vacantRoles' &&
      !queryParams.circleId &&
      !queryParams.memberId
    ) {
      changeParams({ panel: undefined })
    }
  }, [
    highlight,
    queryParams.panel,
    queryParams.circleId,
    queryParams.memberId,
    changeParams,
  ])

  // The vacant roles panel and their emphasis go together, however the panel
  // is opened (right click menu on the background, shared link)
  useEffect(() => {
    if (queryParams.panel === 'vacantRoles') setHighlight('vacant')
  }, [queryParams.panel])

  // Zoom offset to keep the focused circle visible next to the side panel
  const focusCropRight =
    isSidePanel && panel !== Panels.None ? modalPanelWidth : 0
  const focusCrop = useMemo(
    () => ({
      top: 0,
      left: 0,
      right: focusCropRight,
      bottom: 0,
    }),
    [focusCropRight]
  )

  // URL params
  useEffect(() => {
    if (!ready) return

    // Focus circle (use parentId if available, otherwise circleId)
    setCircleId(queryParams.circleId)
    setParentId(queryParams.parentId)

    // Open panel. A selection made from a settings panel (a link in a log
    // entry, a vacant role) takes over the panel, and closing it goes back to
    // the org chart.
    setSettingsPanel(
      isCirclesPanel(queryParams.panel) ? queryParams.panel : undefined
    )
    if (queryParams.memberId) {
      setMemberId(queryParams.memberId)
      setPanel(Panels.Member)
    } else if (queryParams.circleId) {
      setPanel(Panels.Circle)
    } else if (isCirclesPanel(queryParams.panel)) {
      setPanel(Panels.Settings)
    } else {
      setPanel(Panels.None)
    }
  }, [ready, JSON.stringify(queryParams)])

  // When the page scrolls as one (mobile/tablet), scroll back to the top on
  // selection change so the graph, re-focusing on the new circle/member, is
  // visible instead of staying scrolled down in the panel.
  useEffect(() => {
    if (isSidePanel) return
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [circleId, memberId, isSidePanel])

  // Color mode
  const { colorMode } = useColorMode()

  return (
    <GraphProvider>
      <Box
        ref={boxRef}
        id="circles-graph"
        position={isSidePanel ? 'absolute' : 'relative'}
        top={isSidePanel ? 0 : undefined}
        left={isSidePanel ? 0 : undefined}
        bottom={isSidePanel ? 0 : undefined}
        right={isSidePanel ? 0 : undefined}
        h={
          isSidePanel
            ? undefined
            : panel === Panels.None
              ? `calc(100dvh - ${sidebarContext?.height || 0}px)`
              : '50dvh'
        }
        overflow="hidden"
      >
        {org && orgData && boxSize && isRoleLinks && (
          <RoleLinksGraph
            org={orgData}
            width={boxSize.width}
            height={boxSize.height}
            colorMode={colorMode}
            events={events}
            selectedCircleId={circleId}
            focusCrop={focusCrop}
          />
        )}
        {org && isWhiteboard && (
          // Below the options bar, which would cover Excalidraw's toolbar
          <Box position="absolute" top={14} left={0} right={0} bottom={0}>
            <WhiteboardView
              orgId={org.id}
              onSaveStateChange={setWhiteboardSave}
            />
          </Box>
        )}
        {org && orgData && boxSize && !isRoleLinks && !isWhiteboard && (
          <CirclesGraph
            key={`${graphView.view}${graphView.folded}${graphView.members}${colorMode}`}
            view={graphView.view}
            folded={graphView.folded}
            hideMembers={!graphView.members}
            org={orgData}
            events={events}
            width={boxSize.width}
            height={boxSize.height}
            focusCrop={focusCrop}
            selectedCircleId={parentId ? `${parentId}_${circleId}` : circleId}
            highlightedCircleIds={highlightedCircleIds}
            highlightTone={highlight}
            onReady={() => setReady(true)}
          />
        )}
      </Box>

      {contextMenu}

      {panel === Panels.Circle && circleId && (
        <ModalPanel isOpen onClose={handleClosePanel}>
          <CircleProvider circleId={circleId}>
            <CircleContent changeTitle flowHeight={!isSidePanel} />
          </CircleProvider>
        </ModalPanel>
      )}

      {panel === Panels.Member && memberId && (
        <ModalPanel isOpen onClose={handleClosePanel}>
          <MemberContent id={memberId} changeTitle />
        </ModalPanel>
      )}

      {panel === Panels.Settings && settingsPanel && (
        <ModalPanel isOpen onClose={handleClosePanel}>
          <CirclesPanelContent
            panel={settingsPanel}
            changeTitle
            flowHeight={!isSidePanel}
          />
        </ModalPanel>
      )}

      {panel === Panels.None && (
        <Title>{t('CirclesPage.title', { org: org?.name })}</Title>
      )}

      <CirclesGraphOptions
        value={graphView}
        onChange={handleViewChange}
        rightContent={
          isWhiteboard ? (
            <Flex alignItems="center" px={2}>
              <WhiteboardSaveStatus state={whiteboardSave} />
            </Flex>
          ) : undefined
        }
        myRoles={
          myRoles.length > 0
            ? { active: highlight === 'mine', onToggle: handleToggleMyRoles }
            : undefined
        }
        vacantRoles={{
          active: highlight === 'vacant',
          onToggle: handleToggleVacantRoles,
        }}
        position="absolute"
        top={0}
        left={0}
        right={focusCrop.right}
        zIndex={2}
        p={2}
        pl={
          sidebarContext?.minimize.isOpen && !sidebarContext?.isMobile ? 12 : 2
        }
      />
    </GraphProvider>
  )
}
